import {
  Action,
  getModule,
  Module,
  Mutation,
  MutationAction,
  VuexModule,
} from 'vuex-module-decorators';
import {
  concat, filter, find, map, remove, size,
} from 'lodash';
import { classToClass, plainToClass, plainToClassFromExist } from 'class-transformer';
import { validateSync, ValidationError } from 'class-validator';
import repositoryFactory from '@/api/factory';

// eslint-disable-next-line
import store from '@/store';
import { BANK_ACCOUNTS } from '@/constants/repository';
import BankAccountsRepository, { BankAccountDto, UpdateBankAccountDto } from '@/api/bank-accounts';
import { BankAccount } from '@/api/payment-orders';

const bankAccountsRepository = repositoryFactory.get<typeof BankAccountsRepository>(BANK_ACCOUNTS);

export interface BankAccountsState {
  creditors: BankAccount[];
  debtors: BankAccount[];
}

@Module({
  dynamic: true,
  name: BANK_ACCOUNTS,
  namespaced: true,
  store,
})
class BankAccountsModule extends VuexModule {
  bankAccounts: BankAccount[] | null = null;

  get creditors() {
    return this.bankAccounts;
  }

  get debtors() {
    return filter(this.bankAccounts, 'isPayable');
  }

  get findBankAccountById() {
    return (id: string) => find(this.bankAccounts, ['id', id]);
  }

  @Mutation
  private ADD_BANK_ACCOUNT(bankAccountDto: BankAccountDto) {
    const bankAccount = plainToClass(BankAccount, bankAccountDto);

    const validationErrors = validateSync(bankAccount);

    console.log({ validationErrors });

    if (size(validationErrors)) {
      throw new ValidationError();
    }

    this.bankAccounts = concat(this.bankAccounts || [], bankAccount);
  }

  @Mutation
  private UPDATE_BANK_ACCOUNT({ id, ...bankAccountDto }: UpdateBankAccountDto & { id: string }) {
    const bankAccount = find(this.bankAccounts, ['id', id]);

    if (!bankAccount) {
      throw new Error(`No bank account with given id: ${id}`);
    }

    const bankAccountClone = classToClass(bankAccount);

    const updatedBankAccount = plainToClassFromExist(bankAccountClone, bankAccountDto);

    const validationErrors = validateSync(updatedBankAccount);

    console.log({ validationErrors });
    if (size(validationErrors)) {
      throw new ValidationError();
    }

    Object.assign(bankAccount, updatedBankAccount);
  }

  @Mutation
  private DELETE_BANK_ACCOUNT(id: string) {
    if (!this.bankAccounts) {
      console.warn('Bank accounts not loaded');
      return;
    }

    remove(this.bankAccounts, ['id', id]);

    this.bankAccounts = [...this.bankAccounts];
  }

  @MutationAction({ mutate: ['bankAccounts'] })
  async fetchBankAccounts() {
    const { data } = await bankAccountsRepository.get();

    const bankAccounts = map(
      data, (bankAccountDto) => plainToClass(BankAccount, bankAccountDto),
    );

    return { bankAccounts };
  }

  @Action
  loadBankAccounts() {
    if (this.bankAccounts) {
      return null;
    }

    return this.fetchBankAccounts();
  }

  @Action({ rawError: true })
  async saveBankAccount(bankAccount: BankAccount) {
    const { data } = await bankAccountsRepository.post(bankAccount);

    this.ADD_BANK_ACCOUNT(data);

    return this.findBankAccountById(data.id);
  }

  @Action({ rawError: true })
  async updateBankAccount(bankAccount: BankAccount) {
    const { data } = await bankAccountsRepository.put(bankAccount.id, bankAccount);

    this.UPDATE_BANK_ACCOUNT({ id: bankAccount.id, ...data });

    return this.findBankAccountById(bankAccount.id);
  }

  @Action
  async deleteBankAccount(id: string) {
    await bankAccountsRepository.delete(id);

    this.DELETE_BANK_ACCOUNT(id);
  }
}

export default getModule(BankAccountsModule);
